import { makeStyles, MenuItem } from '@material-ui/core';
import { FC, useState, MouseEvent, useEffect } from 'react';
import Garbage from 'src/asset/garbage.svg';
import CButton from 'src/component/Button';
import Modal from 'src/component/Modal/Modal';
import { DefaultSelect } from 'src/component/Select';
import {
  AndroidApp,
  AppleApp
} from 'src/services/napi/projects/interfaces/fetchProjects.interface';
import { isValidSHA256, isValidUrl } from 'src/util/inputValidation';
import { AppType } from 'src/page/DynamicLinksConfig/interfaces/appType.interface';
import { FormErrors } from 'src/page/DynamicLinksConfig/interfaces/formErrors.interface';
import ModalInputGroup from 'src/component/ModalInputGroup';

const useStyle = makeStyles({
  certificate: {
    flex: 'auto',
    fontFamily: 'poppins',
    fontSize: '12px',
    lineHeight: '20px',
    marginBottom: '0px',
    wordBreak: 'break-all'
  },
  certificateRow: {
    alignItems: 'center',
    display: 'flex',
    marginBottom: '6px',
    width: '100%'
  },
  certificatesSectionTitle: {
    borderTop: '1px solid rgba(0, 0, 0, 0.3)',
    fontFamily: 'poppins',
    fontSize: '14px',
    lineHeight: '20px',
    marginBottom: '16px',
    marginTop: '16px',
    paddingTop: '10px'
  },
  form: {
    '& > :nth-last-child(1)': {
      paddingBottom: '24px'
    },
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  garbage: {
    cursor: 'pointer',
    left: '24px',
    position: 'absolute'
  }
});

const DEFAULT_APP_TYPE = 'Select app type';

interface AddOrEditAppModalProps {
  androidApps: AndroidApp[];
  appleApps: AppleApp[];
  handleClose: () => void;
  handleUpdateApps: (apps: { androidApps: AndroidApp[]; appleApps: AppleApp[] }) => void;
  initialDraft?: {
    data: AndroidApp | AppleApp;
    type?: AppType;
  };
  isOpen: boolean;
}

const AddOrEditAppModal: FC<AddOrEditAppModalProps> = ({
  androidApps,
  appleApps,
  handleClose,
  handleUpdateApps,
  initialDraft,
  isOpen
}) => {
  const classes = useStyle();

  const isEditing = !!initialDraft;

  const [isDeleting, setIsDeleting] = useState(false);

  const [appDraft, setAppDraft] = useState<{
    data: AndroidApp | AppleApp;
    type?: AppType;
  }>(
    initialDraft ?? {
      data: { icon: '', name: '' }
    }
  );

  const [sha256CertFingerprint, setSha256CertFingerprint] = useState('');

  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const validateForm = (): FormErrors => {
    if (!appDraft.type) {
      return { form: 'Missing some data' };
    }

    const mergedDraftData: {
      appStoreId?: string;
      bundleId?: string;
      icon?: string;
      name?: string;
      packageName?: string;
      sha256CertFingerprints?: string[];
      teamId?: string;
    } = { ...appDraft.data };

    const {
      icon,
      name,
      packageName,
      sha256CertFingerprints,
      appStoreId,
      bundleId,
      teamId
    } = mergedDraftData;

    const errors: FormErrors = {};

    if (!name?.length) {
      errors.name = 'Name is empty';
    }

    if (!icon?.length) {
      errors.icon = 'Icon is empty';
    } else if (!isValidUrl(icon)) {
      errors.icon = 'Invalid app icon URL';
    }

    if (appDraft.type === AppType.android) {
      if (!packageName?.length) {
        errors.packageName = 'Package name is empty';
      } else if (!isEditing && androidApps.some((app) => app.packageName === packageName)) {
        errors.packageName = 'App with matching package name already exists';
      }

      if (!sha256CertFingerprints?.length) {
        errors.sha256CertFingerprints = 'SHA certificate fingerprints are empty';
      } else if (sha256CertFingerprints.some((cert) => !isValidSHA256(cert))) {
        errors.sha256CertFingerprints = 'Invalid SHA-256 certificate fingerprints';
      }
    }

    if (appDraft.type === AppType.apple) {
      if (!bundleId?.length) {
        errors.bundleId = 'Bundle ID is empty';
      } else if (!isEditing && appleApps.some((app) => app.bundleId === bundleId)) {
        errors.bundleId = 'App with matching bundle ID already exists';
      }

      if (!appStoreId?.length) {
        errors.appStoreId = 'App store ID is empty';
      }

      if (!teamId?.length) {
        errors.teamId = 'Team ID is empty';
      }
    }

    return errors;
  };

  const onSelectNewAppType = (value: AppType | typeof DEFAULT_APP_TYPE) => {
    if (appDraft.type === value) {
      return;
    }

    setAppDraft(({ data: { icon, name } }) => ({
      data: { icon, name },
      type: value === DEFAULT_APP_TYPE ? undefined : value
    }));
  };

  const handleNameChange = (value: string) => {
    if (!/[^a-zA-Z0-9- ]/.test(value)) {
      setAppDraft((prev) => ({
        ...prev,
        data: { ...prev.data, name: value }
      }));
    }
  };

  const handleIconChange = (value: string) => {
    // eslint-disable-next-line no-useless-escape
    if (!/[^-a-zA-Z0-9()@:%_\+.~#?&//=]/.test(value)) {
      setAppDraft((prev) => ({
        ...prev,
        data: { ...prev.data, icon: value }
      }));
    }
  };

  const handlePackageNameChange = (value: string) => {
    if (!/[^a-zA-Z.]/.test(value)) {
      setAppDraft((prev) => ({
        ...prev,
        data: { ...prev.data, packageName: value }
      }));
    }
  };

  const handleSha256CertFingerprintsChange = (value: string) => {
    if (!/[^a-fA-F0-9:]/.test(value)) {
      setSha256CertFingerprint(value);
    }
  };

  const onAddSha256CertFingerprint = () => {
    if (!isValidSHA256(sha256CertFingerprint)) {
      setFormErrors({ sha256CertFingerprints: 'Invalid SHA-256 certificate fingerprint' });
      return;
    }

    if ((appDraft.data as AndroidApp).sha256CertFingerprints?.includes(sha256CertFingerprint)) {
      setFormErrors({ sha256CertFingerprints: 'Certificate already added' });
      return;
    }

    setAppDraft((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        sha256CertFingerprints: [
          ...((prev.data as AndroidApp).sha256CertFingerprints ?? []),
          sha256CertFingerprint
        ]
      }
    }));

    setFormErrors({});
    setSha256CertFingerprint('');
  };

  const onRemoveSha256CertFingerprint = (cert: string) => {
    setAppDraft((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        sha256CertFingerprints: (prev.data as AndroidApp).sha256CertFingerprints?.filter(
          (value) => value !== cert
        )
      }
    }));
  };

  const handleBundleIdChange = (value: string) => {
    if (!/[^a-zA-Z.]/.test(value)) {
      setAppDraft((prev) => ({
        ...prev,
        data: { ...prev.data, bundleId: value }
      }));
    }
  };

  const handleAppStoreIdChange = (value: string) => {
    if (!/[^0-9]/.test(value)) {
      setAppDraft((prev) => ({
        ...prev,
        data: { ...prev.data, appStoreId: value }
      }));
    }
  };

  const handleTeamIdChange = (value: string) => {
    if (!/[^a-zA-Z0-9]/.test(value)) {
      setAppDraft((prev) => ({
        ...prev,
        data: { ...prev.data, teamId: value }
      }));
    }
  };

  const onCancel = (e: MouseEvent) => {
    e.preventDefault();
    handleClose();
  };

  const onDelete = (e: MouseEvent) => {
    e.preventDefault();

    if (!isEditing) {
      return;
    }

    const updatedAndroidApps = [...androidApps];
    const updatedAppleApps = [...appleApps];

    if (appDraft.type === AppType.android) {
      const appData = appDraft.data as AndroidApp;

      const index = updatedAndroidApps.findIndex((app) => app.packageName === appData.packageName);

      if (index !== -1) {
        updatedAndroidApps.splice(index, 1);
      }
    } else {
      const appData = appDraft.data as AppleApp;

      const index = updatedAppleApps.findIndex((app) => app.bundleId === appData.bundleId);

      if (index !== -1) {
        updatedAppleApps.splice(index, 1);
      }
    }

    handleUpdateApps({
      androidApps: updatedAndroidApps,
      appleApps: updatedAppleApps
    });

    handleClose();
  };

  const onSaveApp = (e: MouseEvent) => {
    e.preventDefault();

    if (!appDraft.type) {
      return;
    }

    const errors = validateForm();
    setFormErrors(errors);

    if (Object.keys(errors).length !== 0) {
      return;
    }

    const updatedAndroidApps = [...androidApps];
    const updatedAppleApps = [...appleApps];

    if (appDraft.type === AppType.android) {
      const appData = appDraft.data as AndroidApp;

      if (isEditing) {
        const index = updatedAndroidApps.findIndex(
          (app) => app.packageName === appData.packageName
        );

        if (index !== -1) {
          updatedAndroidApps[index] = appData;
        }
      } else {
        updatedAndroidApps.push(appData);
      }
    } else {
      const appData = appDraft.data as AppleApp;

      if (isEditing) {
        const index = updatedAppleApps.findIndex((app) => app.bundleId === appData.bundleId);

        if (index !== -1) {
          updatedAppleApps[index] = appData;
        }
      } else {
        updatedAppleApps.push(appData);
      }
    }

    handleUpdateApps({
      androidApps: updatedAndroidApps,
      appleApps: updatedAppleApps
    });

    handleClose();
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setAppDraft(
      initialDraft ?? {
        data: { icon: '', name: '' }
      }
    );
    setFormErrors({});
    setSha256CertFingerprint('');
    setIsDeleting(false);
  }, [isOpen]);

  if (isDeleting) {
    return (
      <Modal
        footerActions={
          <>
            <CButton
              onClick={() => setIsDeleting(false)}
              type="secondary"
              xstyle={{ height: '40px' }}
            >
              Cancel
            </CButton>

            <CButton onClick={onDelete} type="primary" xstyle={{ height: '40px' }}>
              Delete
            </CButton>
          </>
        }
        handleClose={() => setIsDeleting(false)}
        isModalOpen={isOpen}
        styles={{
          // @ts-ignore
          '& .MuiDialogContent-root': {
            paddingTop: '0px'
          },
          width: '400px'
        }}
        titleContent={<h3 className="modal-title">Delete App</h3>}
      >
        <h3>
          Are you sure you want to delete this app <strong>{initialDraft?.data?.name ?? ''}</strong>{' '}
          ({initialDraft?.type ?? ''})? You can’t undo this action.
        </h3>
      </Modal>
    );
  }

  return (
    <Modal
      footerActions={
        <>
          {isEditing && (
            <img
              alt="delete app button"
              className={classes.garbage}
              onClick={() => setIsDeleting(true)}
              src={Garbage}
            />
          )}

          <CButton
            htmlType="submit"
            onClick={onCancel}
            type="secondary"
            xstyle={{ height: '40px' }}
          >
            Cancel
          </CButton>

          <CButton htmlType="submit" onClick={onSaveApp} type="primary" xstyle={{ height: '40px' }}>
            {isEditing ? 'Edit' : 'Create'}
          </CButton>
        </>
      }
      handleClose={handleClose}
      isModalOpen={isOpen}
      styles={{
        // @ts-ignore
        '& .MuiDialogContent-root': {
          height: '360px',
          overflowY: 'auto'
        },
        fontSize: '12px',
        width: '540px'
      }}
      titleContent={<h4 className="modal-title">{`${isEditing ? 'Edit' : 'Add New'} App`}</h4>}
    >
      <form className={classes.form} id="new-content-model-form">
        {!isEditing && (
          <DefaultSelect
            defaultValue={DEFAULT_APP_TYPE}
            onSelect={onSelectNewAppType}
            selectStyles={{ marginBottom: '20px' }}
            value={appDraft.type ?? DEFAULT_APP_TYPE}
          >
            {[DEFAULT_APP_TYPE, AppType.android, AppType.apple].map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </DefaultSelect>
        )}

        <ModalInputGroup
          description="App name will be used as default value for dynamic links social preview title."
          error={formErrors.name}
          htmlFor="standard-input-app-name"
          label="App name"
          onChange={handleNameChange}
          value={appDraft.data.name ?? ''}
        />

        <ModalInputGroup
          description="App icon will be used as default value for dynamic links social preview thumbnail image."
          error={formErrors.icon}
          htmlFor="standard-input-app-icon"
          label="App icon"
          onChange={handleIconChange}
          value={appDraft.data.icon ?? ''}
        />

        {appDraft.type === AppType.android && (
          <>
            <ModalInputGroup
              description="Your package name is generally the applicationId in your app-level build.gradle file."
              disabled={isEditing}
              error={formErrors.packageName}
              htmlFor="standard-input-android-package-name"
              label="Android package name"
              onChange={handlePackageNameChange}
              value={(appDraft.data as AndroidApp).packageName ?? ''}
            />

            <p className={classes.certificatesSectionTitle}>SHA-256 certificates:</p>

            {(appDraft.data as AndroidApp).sha256CertFingerprints?.map((cert) => (
              <div key={cert} className={classes.certificateRow}>
                <p className={classes.certificate}>{cert}</p>

                <CButton
                  htmlType="button"
                  onClick={() => onRemoveSha256CertFingerprint(cert)}
                  type="secondary"
                  xstyle={{
                    borderRadius: '12px',
                    fontSize: '12px',
                    height: '32px',
                    lineHeight: '16px'
                  }}
                >
                  Remove
                </CButton>
              </div>
            ))}

            <ModalInputGroup
              anchorHref="https://developers.google.com/android/guides/client-auth"
              anchorText="see more."
              buttonText="Add"
              description="For information on using keytool to get the SHA-256 hash of your signing certificate"
              error={formErrors.sha256CertFingerprints}
              htmlFor="standard-input-android-signing-certificate"
              label="Signing certificate SHA-256"
              onButtonClick={onAddSha256CertFingerprint}
              onChange={handleSha256CertFingerprintsChange}
              value={sha256CertFingerprint}
            />
          </>
        )}

        {appDraft.type === AppType.apple && (
          <>
            <ModalInputGroup
              description="You can find your bundle identifier in the General tab for your app's primary target in Xcode."
              disabled={isEditing}
              error={formErrors.bundleId}
              htmlFor="standard-input-apple-bundle-id"
              label="Apple bundle ID"
              onChange={handleBundleIdChange}
              value={(appDraft.data as AppleApp).bundleId ?? ''}
            />

            <ModalInputGroup
              description="You can find your App Store ID in your app's URL. In the example below, 123456789 is the App Store ID.
              https://itunes.apple.com/us/app/yourapp/id123456789."
              error={formErrors.appStoreId}
              htmlFor="standard-input-apple-app-store-id"
              label="App Store ID"
              onChange={handleAppStoreIdChange}
              value={(appDraft.data as AppleApp).appStoreId ?? ''}
            />

            <ModalInputGroup
              anchorHref="https://developer.apple.com/account/ios/profile"
              anchorText="provisioning profile."
              description="You can find your Team ID in the Apple Member Centre under the"
              error={formErrors.teamId}
              htmlFor="standard-input-apple-team-id"
              label="Team ID"
              onChange={handleTeamIdChange}
              value={(appDraft.data as AppleApp).teamId ?? ''}
            />
          </>
        )}
      </form>
    </Modal>
  );
};

export default AddOrEditAppModal;
